import { Card, CardContent } from '@/components/ui/card';
import { SimulationKPIs, ProfileStatistics } from '@/types/epse';
import { 
  Zap, 
  TrendingDown, 
  AlertTriangle, 
  Battery, 
  Sun, 
  ArrowDownToLine,
  Clock
} from 'lucide-react';

interface EPSEKPICardsProps {
  kpis?: SimulationKPIs;
  statistics?: ProfileStatistics;
  connectionLimit?: number;
}

export function EPSEKPICards({ kpis, statistics, connectionLimit }: EPSEKPICardsProps) {
  const cards = [];
  
  if (statistics) {
    cards.push(
      {
        icon: Zap,
        label: 'Piek (max)',
        value: `${statistics.max.toFixed(0)} kW`,
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
      },
      {
        icon: TrendingDown,
        label: 'P95',
        value: `${statistics.p95.toFixed(0)} kW`,
        description: '95% van de tijd onder dit niveau',
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
      },
      {
        icon: Zap,
        label: 'Gemiddeld',
        value: `${statistics.mean.toFixed(0)} kW`,
        color: 'text-primary',
        bgColor: 'bg-primary/10',
      },
      {
        icon: Battery,
        label: 'Totaal verbruik',
        value: `${Math.round(statistics.total_kwh).toLocaleString('nl-NL')} kWh`,
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
      }
    );
  }
  
  if (kpis) {
    cards.push(
      {
        icon: AlertTriangle,
        label: 'Piek overschrijdingen',
        value: kpis.peak_overruns_count.toString(),
        description: `${kpis.peak_overruns_duration_hours.toFixed(1)} uur totaal`,
        color: kpis.peak_overruns_count > 0 ? 'text-destructive' : 'text-green-500',
        bgColor: kpis.peak_overruns_count > 0 ? 'bg-destructive/10' : 'bg-green-500/10',
      },
      {
        icon: TrendingDown,
        label: 'Piek reductie',
        value: `${kpis.peak_reduction_kw.toFixed(0)} kW`,
        description: `${kpis.peak_reduction_percent.toFixed(1)}% lager`,
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
      },
      {
        icon: Battery,
        label: 'BESS cycli',
        value: kpis.bess_cycles.toFixed(1),
        description: `${(kpis.bess_throughput_kwh / 1000).toFixed(1)} MWh throughput`,
        color: 'text-primary',
        bgColor: 'bg-primary/10',
      },
      {
        icon: ArrowDownToLine,
        label: 'Netimport',
        value: `${Math.round(kpis.import_kwh).toLocaleString('nl-NL')} kWh`,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
      }
    );
    
    if (kpis.self_consumed_pv_kwh > 0) {
      cards.push({
        icon: Sun,
        label: 'Eigen verbruik PV',
        value: `${Math.round(kpis.self_consumed_pv_kwh).toLocaleString('nl-NL')} kWh`,
        description: kpis.curtailed_pv_kwh > 0 
          ? `${Math.round(kpis.curtailed_pv_kwh).toLocaleString('nl-NL')} kWh gecurtailed`
          : undefined,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500/10',
      });
    }
  }
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <Card key={i}>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <card.icon className={`h-4 w-4 ${card.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground truncate">{card.label}</p>
                <p className="text-xl font-semibold">{card.value}</p>
                {card.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{card.description}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
